import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import { setupIntersectionObserver } from 'utils/observerUtil';

const useStyles = makeStyles((theme) => ({
  clientContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    // No rounded cap: this now sits below the testimonials, not directly under
    // the hero gradient, so corners here would expose the dark backdrop.
    backgroundColor: theme.palette.shades['clr-white-900'],
    borderBottom: `2px solid ${theme.palette.divider}`,
    height: '100%',
    width: '100%',
    padding: theme.spacing(15, 37.5, 23, 37.5),
    gap: theme.spacing(23),
    transition: 'transform 0.4s ease-in-out, opacity 1s ease-in-out',
    transform: 'translateY(50px)',
    opacity: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(15, 15, 23, 15),
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(15, 15),
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(6, 10, 9.5, 10),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(6, 5, 9.5, 5),
    },
  },
  backgroundImage: {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'url("https://assets.languify.in/images/square_white.png")',
    backgroundRepeat: 'repeat',
    backgroundSize: 'auto',
    opacity: 0.4,
    zIndex: -1,
  },
  clientBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(23),
    width: '100%',
    overflow: 'hidden',
    [theme.breakpoints.down('800')]: {
      gap: theme.spacing(11),
    },
  },
  gridContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  gridRow: {
    display: 'flex',
    width: '100%',
  },
  gridItem: {
    width: '33%',
    height: '104px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px dashed',
    borderColor: theme.palette.neutral.clr_400,
    [theme.breakpoints.between('md', 'lg')]: {
      width: '33%',
    },
  },
  image: {
    width: '170px',
    // Widths below are the design; the height cap only engages for logos that
    // are squarer than the ~2:1 these cells assume (dc-consulting is 1:1, and
    // would otherwise render 170px tall in a 104px cell). `contain` keeps the
    // aspect ratio intact when it does.
    maxHeight: '88px',
    objectFit: 'contain',
    [theme.breakpoints.between('sm', 'md')]: {
      width: '120px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100px',
    },
  },
}));

const CaseClientsSection = () => {
  const classes = useStyles();

  const [visible, setVisible] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    if (window.innerHeight > 1700) {
      setVisible(true);
      return;
    }

    const cleanup = setupIntersectionObserver(containerRef, setVisible, 0.2);

    return cleanup;
  }, []);

  // Case Master has its own set of clients, distinct from the B2B landing page.
  const boxes = [
    {
      image: `${process.env.REACT_APP_ASSETS_URL}/images/IIMU_b2c.png`,
      alt: 'IIM Udaipur',
    },
    {
      image: `${process.env.REACT_APP_ASSETS_URL}/images/SPJ_b2c.png`,
      alt: 'SP Jain',
    },
    {
      image: `${process.env.REACT_APP_ASSETS_URL}/images/Somaiya_b2c.png`,
      alt: 'KJ Somaiya',
    },
    {
      image: `${process.env.REACT_APP_ASSETS_URL}/images/dc-consulting.jpeg`,
      alt: 'DC Consulting',
    },
    {
      image: `${process.env.REACT_APP_ASSETS_URL}/images/mesa.png`,
      alt: 'Mesa',
    },
    {
      image: `${process.env.REACT_APP_ASSETS_URL}/images/fiib.png`,
      alt: 'FIIB',
    },
  ];

  return (
    <Box
      ref={containerRef}
      className={classes.clientContainer}
      style={{
        transform: visible ? 'translateY(0)' : 'translateY(50px)',
        opacity: visible ? 1 : 0,
      }}
    >
      <Box className={classes.backgroundImage} />
      <Box className={classes.clientBox}>
        <Typography variant="h3-bold" align="center" style={{ color: '#2E3558' }}>
          Trusted by <span style={{ color: '#0051ff' }}>30+</span> institutions
          <br />
          and <span style={{ color: '#0051ff' }}>1 lakh+</span> students
        </Typography>
        <Box className={classes.gridContainer}>
          {[0, 1].map((row) => (
            <Box className={classes.gridRow} key={row}>
              {boxes.slice(row * 3, row * 3 + 3).map((box, index) => {
                const isTopRow = row === 0;
                const isBottomRow = row === 1;
                const isFirstColumn = index === 0;
                const isLastColumn = index === 2;

                return (
                  <Box
                    key={index}
                    className={classes.gridItem}
                    style={{
                      borderTop: isTopRow ? '1px dashed transparent' : `1px dashed #8692A3`,
                      borderBottom: isBottomRow ? '1px dashed transparent' : `1px dashed #8692A3`,
                      borderLeft: isFirstColumn ? '1px dashed transparent' : `1px dashed #8692A3`,
                      borderRight: isLastColumn ? '1px dashed transparent' : `1px dashed #8692A3`,
                    }}
                  >
                    <img className={classes.image} src={box.image} alt={box.alt} />
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default CaseClientsSection;
